<template>
  <section class="flex min-w-0 flex-col gap-3">
    <div class="flex items-center justify-between gap-3">
      <h2 class="font-display text-[22px] font-extrabold sm:text-[26px]">Der Kader</h2>
      <!-- Pfeile für Maus-Bedienung (Desktop) -->
      <div class="hidden gap-2 md:flex">
        <button
          v-for="nav in navButtons"
          :key="nav.dir"
          @click="scrollByPage(nav.dir)"
          :disabled="nav.disabled"
          :aria-label="nav.label"
          class="flex h-11 w-11 items-center justify-center rounded-xl border border-edge bg-panel text-slate-100 transition-colors hover:border-beer hover:text-beer disabled:cursor-default disabled:opacity-40 disabled:hover:border-edge disabled:hover:text-slate-100"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path :d="nav.dir < 0 ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'" />
          </svg>
        </button>
      </div>
    </div>
    <ul
      ref="track"
      @scroll.passive="updateEdges"
      @wheel="onWheel"
      class="-mx-4 flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-smooth px-4 pb-2 pt-1 [scrollbar-width:none] sm:mx-0 sm:px-0"
    >
      <li v-for="member in squad" :key="member.id" class="relative w-[108px] flex-none snap-start">
        <button
          type="button"
          @click="selected = member"
          class="group flex w-full flex-col items-center gap-2 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-beer"
          :aria-label="`${member.firstName}, Trikotnummer ${member.shirtNumber} – Foto anzeigen`"
        >
          <div class="relative h-[120px] w-[108px] transition-transform duration-200 group-hover:-translate-y-1" aria-hidden="true">
            <div class="absolute left-0 top-2 h-10 w-8 rotate-[18deg] rounded-l-[10px] rounded-r" :style="{ backgroundColor: member.color }"></div>
            <div class="absolute right-0 top-2 h-10 w-8 -rotate-[18deg] rounded-l rounded-r-[10px]" :style="{ backgroundColor: member.color }"></div>
            <div class="absolute inset-y-0 left-5 right-5 flex items-center justify-center rounded-b-[14px] rounded-t-[10px]" :style="{ backgroundColor: member.color }">
              <span class="absolute -top-px h-3 w-7 rounded-b-[14px] bg-night"></span>
              <span class="font-display text-[30px] font-extrabold" :style="{ color: readableTextOn(member.color) }">{{ member.shirtNumber }}</span>
            </div>
          </div>
          <span class="text-sm font-medium group-hover:text-beer" aria-hidden="true">{{ member.firstName }}</span>
        </button>
      </li>
    </ul>
    <MemberPhotoDialog :member="selected" @close="selected = null" />
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import Member from '@/types/Member';
import { readableTextOn } from '@/utils/format';
import MemberPhotoDialog from '@/components/home/MemberPhotoDialog.vue';

const props = defineProps<{ members: Member[] }>();

// Nur Mitglieder mit Trikotnummer (Gäste haben 0), sortiert nach Nummer
const squad = computed(() =>
  props.members.filter((m) => m.shirtNumber !== 0).sort((a, b) => a.shirtNumber - b.shirtNumber),
);

// Angeklicktes Trikot: zeigt Foto (oder Logo) im Dialog
const selected = ref<Member | null>(null);

const track = ref<HTMLElement | null>(null);
const atStart = ref(true);
const atEnd = ref(false);

const updateEdges = () => {
  const el = track.value;
  if (!el) return;
  atStart.value = el.scrollLeft <= 2;
  atEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
};

// Eine "Seite" weiter: gut drei Viertel der sichtbaren Breite, damit ein Trikot zur Orientierung stehen bleibt
const scrollByPage = (dir: number) => {
  const el = track.value;
  if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8 });
};

// Senkrechtes Mausrad über dem Kader horizontal scrollen, solange es noch etwas zu scrollen gibt
const onWheel = (event: WheelEvent) => {
  const el = track.value;
  if (!el || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
  const canScroll = event.deltaY > 0 ? !atEnd.value : !atStart.value;
  if (!canScroll) return;
  event.preventDefault();
  el.scrollLeft += event.deltaY;
};

const navButtons = computed(() => [
  { dir: -1, label: 'Vorherige Trikots', disabled: atStart.value },
  { dir: 1, label: 'Nächste Trikots', disabled: atEnd.value },
]);

onMounted(() => {
  updateEdges();
  window.addEventListener('resize', updateEdges);
});
onUnmounted(() => window.removeEventListener('resize', updateEdges));
watch(squad, () => nextTick(updateEdges));
</script>
